import { Vector3 } from "three";
import type { ClashSettings } from "./cad-clash-detection";

type Sample = { point: Vector3; spacing?: number; eligible: boolean };
type FitResult =
  | {
      normal: { x: number; y: number; z: number };
      point: { x: number; y: number; z: number };
    }
  | null
  | undefined;

export const CROSSING_LIMITS = {
  samples: 100_000,
  fits: 10_000,
  checks: 4_000_000,
  neighbors: 128,
  minimumSupport: 8,
  radius: 0.05,
  normalRadius: 0.02,
  minimumInlierRatio: 0.8,
};

/** Bounded, camera-independent neighbourhoods for actual scan-surface evidence. */
export class ClashSurfaceSamples {
  private readonly samples = new Map<string, Sample>();
  limited = false;
  insufficientSupport = false;

  /** @param settings Frozen sampling and matching parameters. */
  constructor(private readonly settings: ClashSettings) {}

  /** @returns Number of retained occupied scan cells. */
  get size(): number {
    return this.samples.size;
  }

  /**
   * @param point Owned-frame scan position.
   * @param spacing Nominal node spacing, when known.
   * @param eligible Whether this sample may seed evidence rather than only support a neighbour.
   */
  add(point: Vector3, spacing?: number, eligible = true): void {
    const key = point
      .toArray()
      .map((value) => Math.floor(value / this.settings.resolution))
      .join(",");
    const knownSpacing =
      spacing !== undefined && Number.isFinite(spacing) && spacing > 0
        ? spacing
        : undefined;
    const previous = this.samples.get(key);
    if (previous) {
      previous.eligible ||= eligible;
      if (knownSpacing !== undefined) {
        previous.spacing = Math.min(previous.spacing ?? Infinity, knownSpacing);
      }
      return;
    }
    if (this.samples.size >= CROSSING_LIMITS.samples) {
      this.limited = true;
      return;
    }
    this.samples.set(key, {
      point: point.clone(),
      spacing: knownSpacing,
      eligible,
    });
  }

  /**
   * @param fitPlane Existing native plane fitter, operating on recentered samples.
   * @param visit Consumer of a supported scan patch and its measured samples.
   * @param includeSeed Optional work-region filter; neighbouring context is retained for fitting.
   * @param reportUnsupported Whether missing support at this seed belongs to the owned region.
   */
  forEachPatch(
    fitPlane: (positions: Float32Array) => FitResult,
    visit: (
      sample: Sample,
      normal: Vector3,
      neighbors: Vector3[],
    ) => boolean | void,
    includeSeed: (point: Vector3) => boolean = () => true,
    reportUnsupported: (point: Vector3) => boolean = includeSeed,
  ): void {
    const { resolution, tolerance } = this.settings;
    const radius = Math.min(
      CROSSING_LIMITS.radius,
      Math.max(resolution * 4, tolerance * 3),
    );
    const edge = CROSSING_LIMITS.normalRadius;
    const buckets = new Map<string, Sample[]>();
    for (const sample of this.samples.values()) {
      const key = sample.point
        .toArray()
        .map((value) => Math.floor(value / edge))
        .join(",");
      const bucket = buckets.get(key);
      if (bucket) bucket.push(sample);
      else buckets.set(key, [sample]);
    }
    let checks = 0;
    let fits = 0;
    for (const sample of this.samples.values()) {
      if (!sample.eligible || !includeSeed(sample.point)) continue;
      if (fits >= CROSSING_LIMITS.fits) {
        this.limited = true;
        break;
      }
      const neighbors: Array<{ point: Vector3; distance: number }> = [];
      const minimum = sample.point
        .toArray()
        .map((value) => Math.floor((value - radius) / edge));
      const maximum = sample.point
        .toArray()
        .map((value) => Math.floor((value + radius) / edge));
      for (let axisX = minimum[0]; axisX <= maximum[0]; axisX++) {
        for (let axisY = minimum[1]; axisY <= maximum[1]; axisY++) {
          for (let axisZ = minimum[2]; axisZ <= maximum[2]; axisZ++) {
            for (const other of buckets.get(`${axisX},${axisY},${axisZ}`) ??
              []) {
              if (++checks > CROSSING_LIMITS.checks) {
                this.limited = true;
                return;
              }
              const distance = sample.point.distanceToSquared(other.point);
              if (distance <= radius ** 2) {
                neighbors.push({ point: other.point, distance });
              }
            }
          }
        }
      }
      neighbors.sort((left, right) => left.distance - right.distance);
      if (neighbors.length > CROSSING_LIMITS.neighbors) {
        neighbors.length = CROSSING_LIMITS.neighbors;
      }
      const normalRadius = Math.min(
        CROSSING_LIMITS.normalRadius,
        Math.max(resolution * 3, (sample.spacing ?? resolution) * 2),
      );
      const local = neighbors.filter(
        ({ distance }) => distance <= normalRadius ** 2,
      );
      if (local.length < CROSSING_LIMITS.minimumSupport) {
        this.insufficientSupport ||= reportUnsupported(sample.point);
        continue;
      }
      fits++;
      const fit = fitPlane(
        new Float32Array(
          local.flatMap(({ point }) =>
            point.clone().sub(sample.point).toArray(),
          ),
        ),
      );
      if (!fit) {
        this.insufficientSupport ||= reportUnsupported(sample.point);
        continue;
      }
      const normal = new Vector3(fit.normal.x, fit.normal.y, fit.normal.z);
      const origin = new Vector3(fit.point.x, fit.point.y, fit.point.z).add(
        sample.point,
      );
      if (
        !normal.toArray().every(Number.isFinite) ||
        normal.lengthSq() < 0.5 ||
        !origin.toArray().every(Number.isFinite)
      ) {
        this.insufficientSupport ||= reportUnsupported(sample.point);
        continue;
      }
      normal.normalize();
      const residualLimit = resolution * 0.5;
      function inPlane(point: Vector3): boolean {
        return Math.abs(point.clone().sub(origin).dot(normal)) <= residualLimit;
      }
      if (
        !inPlane(sample.point) ||
        local.filter(({ point }) => inPlane(point)).length <
          local.length * CROSSING_LIMITS.minimumInlierRatio
      ) {
        this.insufficientSupport ||= reportUnsupported(sample.point);
        continue;
      }
      const tangent = local[local.length - 1].point
        .clone()
        .sub(origin)
        .projectOnPlane(normal)
        .normalize();
      const perpendicular = new Vector3().crossVectors(normal, tangent);
      const spread =
        local.reduce(
          (sum, { point }) =>
            sum + point.clone().sub(origin).dot(perpendicular) ** 2,
          0,
        ) / local.length;
      if (spread < residualLimit ** 2) {
        this.insufficientSupport ||= reportUnsupported(sample.point);
        continue;
      }
      if (
        visit(
          sample,
          normal,
          neighbors
            .filter(({ point }) => inPlane(point))
            .map(({ point }) => point),
        ) === false
      ) {
        return;
      }
    }
  }
}
